import { ReactNode } from 'react'
import { Lead } from '../../services/leads'
import { TCGS } from '../../constants/tcgs'
import { SOCIALS, SEGMENTS, SocialId } from '../../constants/socials'
import { TcgLogo } from '../../components/tcg/TcgLogo'

type Props = { lead: Lead; onClick: () => void }

export function LeadCard({ lead, onClick }: Props) {
  const tcgs = TCGS.filter((t) => lead.tcgs.includes(t.id))
  const segments = SEGMENTS.filter((s) => lead.segments.includes(s.id))

  const activeSocials: { id: SocialId; glyph: string; label: string }[] = []
  if (lead.facebook) {
    const fb = SOCIALS.find((s) => s.id === 'facebook')!
    activeSocials.push({ id: fb.id, glyph: fb.glyph, label: fb.label })
  }
  for (const s of SOCIALS) {
    if (s.type === 'username' && (lead.socials[s.id] ?? '').trim()) {
      activeSocials.push({ id: s.id, glyph: s.glyph, label: s.label })
    }
  }

  const hasContactIcons = !!lead.email || !!lead.phone
  const hasIconRow = tcgs.length > 0 || hasContactIcons || activeSocials.length > 0

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full rounded-2xl bg-white/[0.04] px-4 py-3 text-left ring-1 ring-white/5 transition-colors hover:bg-white/[0.07]"
    >
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="truncate text-[15px] font-semibold">
            {lead.name || (
              <span className="italic text-theme-text-muted">No name</span>
            )}
          </div>
        </div>
        <ChevronRight />
      </div>

      {hasIconRow && (
        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5">
          {tcgs.length > 0 && (
            <div className="flex -space-x-1">
              {tcgs.map((t) => (
                <div
                  key={t.id}
                  title={t.name}
                  className="flex h-6 w-6 items-center justify-center rounded-md bg-white/[0.06] ring-1 ring-black/40"
                >
                  <TcgLogo
                    tcg={t}
                    imgClassName="max-h-[18px] max-w-[18px] object-contain"
                    fallbackClassName="text-[8px]"
                  />
                </div>
              ))}
            </div>
          )}

          {tcgs.length > 0 && (hasContactIcons || activeSocials.length > 0) && (
            <span className="h-3 w-px bg-white/10" />
          )}

          {lead.email && (
            <IconPip title="Email">
              <MailIcon />
            </IconPip>
          )}
          {lead.phone && (
            <IconPip title="Phone">
              <PhoneIcon />
            </IconPip>
          )}

          {activeSocials.map((s) => (
            <IconPip key={s.id} title={s.label}>
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                className="h-3 w-3"
                aria-hidden
              >
                <path d={s.glyph} />
              </svg>
            </IconPip>
          ))}
        </div>
      )}

      {segments.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {segments.map((s) => (
            <span
              key={s.id}
              className="rounded-full bg-primary-500/15 px-2 py-0.5 text-[10px] font-medium text-primary-200 ring-1 ring-primary-500/30"
            >
              {s.label}
            </span>
          ))}
        </div>
      )}
    </button>
  )
}

function IconPip({
  children,
  title,
}: {
  children: ReactNode
  title?: string
}) {
  return (
    <div
      title={title}
      className="flex h-5 w-5 items-center justify-center rounded-full bg-white/[0.06] text-theme-text-subtle"
    >
      {children}
    </div>
  )
}

function ChevronRight() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-theme-text-muted"
      aria-hidden
    >
      <path d="M9 18l6-6-6-6" />
    </svg>
  )
}

function MailIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="12"
      height="12"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </svg>
  )
}

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" aria-hidden>
      <path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.05-.24c1.16.39 2.4.6 3.71.6.55 0 1 .45 1 1V20a1 1 0 0 1-1 1A18 18 0 0 1 3 5a1 1 0 0 1 1-1h3.5c.55 0 1 .45 1 1 0 1.31.21 2.55.6 3.71a1 1 0 0 1-.25 1.05l-2.23 2.03z" />
    </svg>
  )
}
